import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Layout from '@/components/Layout';
import { getCompanyBySlug, companyPath, ROUTE_PATHS } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function CompanyAbout() {
  const { slug } = useParams<{ slug: string }>();
  const company = getCompanyBySlug(slug ?? '');
  if (!company) return <Layout><div className="min-h-screen flex items-center justify-center"><Link to={ROUTE_PATHS.COMPANIES} className="btn-gold-outline">Back</Link></div></Layout>;

  return (
    <Layout activeCompanySlug={company.slug}>
      {/* Hero */}
      <section className="relative pt-40 pb-24 min-h-[55vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img src={company.heroImage} alt={company.name} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-4">{company.name}</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,5.5rem)] text-[#f5f5f5] max-w-3xl">
              Our <span className="text-gold italic">Story</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* About content */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            <FadeUp>
              <p className="eyebrow mb-4">Who We Are</p>
              <h2 className="heading-display text-[clamp(2rem,3.5vw,3rem)] text-[#f5f5f5] mb-6">{company.tagline}</h2>
              <div className="divider-gold-left" />
              <p className="text-[#f5f5f5]/55 text-sm leading-loose mt-6">{company.longDescription}</p>

              <div className="grid grid-cols-2 gap-5 mt-10">
                {company.stats.map((stat, i) => (
                  <div key={i} className="border border-[rgba(201,168,76,0.12)] p-5">
                    <p className="heading-display text-3xl text-gold mb-1">{stat.value}</p>
                    <p className="eyebrow text-[9px] opacity-55">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mt-8">
                <Link to={companyPath(company.slug) + '/services'} className="btn-gold-fill">Our Services</Link>
                <Link to={companyPath(company.slug) + '/contact'} className="btn-gold-outline">Contact Us</Link>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="relative">
                <div className="card-editorial h-[500px]">
                  <img src={company.heroImage} alt={company.name} />
                  <div className="card-overlay" />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-[#0e0e0e] border border-[rgba(201,168,76,0.2)] p-6 shadow-xl max-w-[180px]">
                  <p className="heading-display text-3xl text-gold mb-1">{company.stats[0].value}</p>
                  <p className="eyebrow text-[9px] opacity-55">{company.stats[0].label}</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#060606]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-12">
            <h2 className="heading-display text-3xl text-[#f5f5f5]">What We Stand For</h2>
            <div className="divider-gold mt-4" />
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(201,168,76,0.08)]">
            {[
              { t: 'Excellence', d: 'We set the highest bar in everything we do — for our clients, our productions, and our brand.' },
              { t: 'Innovation', d: 'We constantly evolve our methods, embracing new ideas while honoring proven principles.' },
              { t: 'Impact', d: 'Every engagement is designed to deliver measurable, lasting results that elevate everyone involved.' },
            ].map((v, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="bg-[#090909] p-9">
                  <span className="heading-display text-4xl text-gold opacity-15 block mb-3">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="heading-display text-xl text-[#f5f5f5] mb-3">{v.t}</h3>
                  <div className="divider-gold-left" />
                  <p className="text-[#f5f5f5]/50 text-sm leading-relaxed mt-4">{v.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#080808] text-center">
        <FadeUp>
          <p className="eyebrow mb-5">Work Together</p>
          <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5] mb-8">Let's Build Something Great</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={companyPath(company.slug) + '/contact'} className="btn-gold-fill">Contact Us</Link>
            <Link to={ROUTE_PATHS.HOME} className="btn-gold-outline">Back to Hub</Link>
          </div>
        </FadeUp>
      </section>
    </Layout>
  );
}
